import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FormData {
  fullName: string;
  email: string;
  businessName: string;
  hasWebsite: string;
  websiteLink: string;
  noWebsiteRedirect: string;
  hasDomain: string;
  projectDetails: string;
  hostingPlan: string;
  generatingRevenue: string;
  hostingCostPlan: string[];
  hostingCostOther: string;
  launchDate: string;
  agreedToTerms: boolean;
}

interface WelcomePopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WelcomePopup({ isOpen, onClose }: WelcomePopupProps) {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    businessName: '',
    hasWebsite: '',
    websiteLink: '',
    noWebsiteRedirect: '',
    hasDomain: '',
    projectDetails: '',
    hostingPlan: '',
    generatingRevenue: '',
    hostingCostPlan: [],
    hostingCostOther: '',
    launchDate: '',
    agreedToTerms: false,
  });

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setFormSubmitted(false);
      setSubmitError('');
      setFormData({
        fullName: '',
        email: '',
        businessName: '',
        hasWebsite: '',
        websiteLink: '',
        noWebsiteRedirect: '',
        hasDomain: '',
        projectDetails: '',
        hostingPlan: '',
        generatingRevenue: '',
        hostingCostPlan: [],
        hostingCostOther: '',
        launchDate: '',
        agreedToTerms: false,
      });
    }, 300);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.agreedToTerms) return;

    setIsSubmitting(true);
    setSubmitError('');

    const params = new URLSearchParams();
    params.append('formID', '260534658051052');
    params.append('q28_fullName', formData.fullName);
    params.append('q3_q3_email1', formData.email);
    params.append('q30_businessorganizationName', formData.businessName);
    params.append('q8_doYou', formData.hasWebsite);
    if (formData.hasWebsite === 'Yes') {
      params.append('q10_pleaseShare', formData.websiteLink);
    }
    if (formData.hasWebsite === 'No') {
      params.append('q31_sinceYou', formData.noWebsiteRedirect);
    }
    params.append('q9_haveYou', formData.hasDomain);
    params.append('q43_tellMe', formData.projectDetails);
    params.append('q36_whichHosting', formData.hostingPlan);
    params.append('q32_Is', formData.generatingRevenue);
    if (formData.generatingRevenue === 'Not yet') {
      formData.hostingCostPlan.forEach((val) => {
        if (val !== 'other') params.append('q34_howDo[]', val);
      });
      if (formData.hostingCostPlan.includes('other') && formData.hostingCostOther) {
        params.append('q34_howDo[other]', formData.hostingCostOther);
      }
    }
    params.append('q35_whenDo', formData.launchDate);
    params.append('simple_spc', '260534658051052-260534658051052');

    try {
      await fetch('https://submit.jotform.com/submit/260534658051052', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
        mode: 'no-cors',
      });

      setFormSubmitted(true);
      setTimeout(() => handleClose(), 3500);
    } catch {
      setSubmitError('Something went wrong. Please try again or email me directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F5C518]/50 focus:bg-white/[0.07] transition-all';
  const selectClass = `${inputClass} appearance-none cursor-pointer`;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={handleClose}
      style={{ animation: 'modalFadeIn 0.25s ease-out forwards' }}
    >
      <div
        className="relative bg-gradient-to-br from-[#1a1a1a] via-[#1f1f1f] to-[#1a1a1a] rounded-2xl shadow-2xl max-w-lg w-full border border-white/10 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'modalSlideUp 0.3s ease-out forwards' }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white/80 transition-colors z-10 cursor-pointer w-8 h-8 flex items-center justify-center"
          aria-label="Close popup"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {/* Header */}
        <div className="px-6 sm:px-8 pt-8 pb-4">
          <div className="inline-flex items-center gap-2 bg-[#F5C518]/10 border border-[#F5C518]/20 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-message-3-line text-[#F5C518] text-sm"></i>
            <span className="text-[#F5C518] text-xs font-semibold tracking-wide uppercase">Get In Touch</span>
          </div>
          <h2 className="font-display text-white text-3xl sm:text-4xl tracking-wide leading-tight mb-3">
            LET&apos;S WORK <span className="text-[#F5C518]">TOGETHER</span>
          </h2>
          <p className="text-white/50 text-sm leading-relaxed mb-6">
            Ready to bring your vision to life? Fill out the details below and I&apos;ll get back to you as soon as possible to discuss your project.
          </p>
        </div>

        {!formSubmitted ? (
          <form id="jotform-inquiry-form" onSubmit={handleSubmit} className="px-6 sm:px-8 pb-8">
            <div className="space-y-3">

              {/* Row: Full Name + Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className={inputClass}
                />
                <input
                  type="email"
                  placeholder="Email Address *"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={inputClass}
                />
              </div>

              {/* Business Name */}
              <input
                type="text"
                placeholder="Business / Organization Name"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                className={inputClass}
              />

              {/* Row: Has website + Domain */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                  <select
                    required
                    value={formData.hasWebsite}
                    onChange={(e) => setFormData({ ...formData, hasWebsite: e.target.value, websiteLink: '', noWebsiteRedirect: '' })}
                    className={selectClass}
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" disabled className="bg-[#1a1a1a]">Have a website?</option>
                    <option value="Yes" className="bg-[#1a1a1a]">Yes</option>
                    <option value="No" className="bg-[#1a1a1a]">No</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-white/40 text-base"></i>
                  </div>
                </div>
                <div className="relative">
                  <select
                    value={formData.hasDomain}
                    onChange={(e) => setFormData({ ...formData, hasDomain: e.target.value })}
                    className={selectClass}
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-[#1a1a1a]">Domain purchased?</option>
                    <option value="Yes" className="bg-[#1a1a1a]">Yes</option>
                    <option value="No" className="bg-[#1a1a1a]">No</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-white/40 text-base"></i>
                  </div>
                </div>
              </div>

              {/* Conditional: website link */}
              {formData.hasWebsite === 'Yes' && (
                <input
                  type="text"
                  placeholder="Share your current website link"
                  value={formData.websiteLink}
                  onChange={(e) => setFormData({ ...formData, websiteLink: e.target.value })}
                  className={inputClass}
                />
              )}

              {/* Conditional: no website redirect */}
              {formData.hasWebsite === 'No' && (
                <input
                  type="text"
                  placeholder="Where are you currently directing customers?"
                  value={formData.noWebsiteRedirect}
                  onChange={(e) => setFormData({ ...formData, noWebsiteRedirect: e.target.value })}
                  className={inputClass}
                />
              )}

              {/* Project Details */}
              <div className="relative">
                <textarea
                  placeholder="Tell me about your business / organization / project"
                  required
                  maxLength={500}
                  value={formData.projectDetails}
                  onChange={(e) => setFormData({ ...formData, projectDetails: e.target.value })}
                  className={`${inputClass} resize-none h-24`}
                />
                <span className="absolute bottom-2 right-3 text-white/20 text-xs pointer-events-none">
                  {formData.projectDetails.length}/500
                </span>
              </div>

              {/* Row: Hosting Plan + Revenue */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="relative">
                  <select
                    value={formData.hostingPlan}
                    onChange={(e) => setFormData({ ...formData, hostingPlan: e.target.value })}
                    className={selectClass}
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-[#1a1a1a]">Hosting plan?</option>
                    <option value="Monthly — $59.99/month" className="bg-[#1a1a1a]">Monthly — $59.99/mo</option>
                    <option value="Yearly — $499.99/year (save $219)" className="bg-[#1a1a1a]">Yearly — $499.99/yr</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-white/40 text-base"></i>
                  </div>
                </div>
                <div className="relative">
                  <select
                    value={formData.generatingRevenue}
                    onChange={(e) => setFormData({ ...formData, generatingRevenue: e.target.value })}
                    className={selectClass}
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" className="bg-[#1a1a1a]">Generating revenue?</option>
                    <option value="Yes" className="bg-[#1a1a1a]">Yes</option>
                    <option value="Not yet" className="bg-[#1a1a1a]">Not yet</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-white/40 text-base"></i>
                  </div>
                </div>
              </div>

              {/* Conditional: how do you plan to maintain hosting costs */}
              {formData.generatingRevenue === 'Not yet' && (
                <div className="bg-white/[0.03] border border-white/10 rounded-lg px-4 py-4">
                  <p className="text-white/60 text-xs font-semibold uppercase tracking-wide mb-3">
                    How do you plan to maintain hosting costs?
                  </p>
                  <div className="space-y-2">
                    {['Personal income', 'Savings', "I'm not sure yet"].map((option) => (
                      <label key={option} className="flex items-center gap-3 cursor-pointer group">
                        <div
                          className={`w-[16px] h-[16px] rounded border flex-shrink-0 flex items-center justify-center transition-all ${
                            formData.hostingCostPlan.includes(option)
                              ? 'bg-[#F5C518] border-[#F5C518]'
                              : 'border-white/20 bg-white/5 group-hover:border-white/40'
                          }`}
                          onClick={() => {
                            const updated = formData.hostingCostPlan.includes(option)
                              ? formData.hostingCostPlan.filter((v) => v !== option)
                              : [...formData.hostingCostPlan, option];
                            setFormData({ ...formData, hostingCostPlan: updated });
                          }}
                        >
                          {formData.hostingCostPlan.includes(option) && (
                            <i className="ri-check-line text-[#1a1a1a] text-[10px]"></i>
                          )}
                        </div>
                        <span
                          className="text-white/60 text-sm cursor-pointer"
                          onClick={() => {
                            const updated = formData.hostingCostPlan.includes(option)
                              ? formData.hostingCostPlan.filter((v) => v !== option)
                              : [...formData.hostingCostPlan, option];
                            setFormData({ ...formData, hostingCostPlan: updated });
                          }}
                        >
                          {option}
                        </span>
                      </label>
                    ))}
                    {/* Other */}
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div
                        className={`w-[16px] h-[16px] rounded border flex-shrink-0 flex items-center justify-center transition-all ${
                          formData.hostingCostPlan.includes('other')
                            ? 'bg-[#F5C518] border-[#F5C518]'
                            : 'border-white/20 bg-white/5 group-hover:border-white/40'
                        }`}
                        onClick={() => {
                          const updated = formData.hostingCostPlan.includes('other')
                            ? formData.hostingCostPlan.filter((v) => v !== 'other')
                            : [...formData.hostingCostPlan, 'other'];
                          setFormData({ ...formData, hostingCostPlan: updated });
                        }}
                      >
                        {formData.hostingCostPlan.includes('other') && (
                          <i className="ri-check-line text-[#1a1a1a] text-[10px]"></i>
                        )}
                      </div>
                      <span
                        className="text-white/60 text-sm cursor-pointer"
                        onClick={() => {
                          const updated = formData.hostingCostPlan.includes('other')
                            ? formData.hostingCostPlan.filter((v) => v !== 'other')
                            : [...formData.hostingCostPlan, 'other'];
                          setFormData({ ...formData, hostingCostPlan: updated });
                        }}
                      >
                        Other
                      </span>
                    </label>
                    {formData.hostingCostPlan.includes('other') && (
                      <input
                        type="text"
                        placeholder="Please specify..."
                        value={formData.hostingCostOther}
                        onChange={(e) => setFormData({ ...formData, hostingCostOther: e.target.value })}
                        className={`${inputClass} mt-1`}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Launch Date */}
              <input
                type="text"
                placeholder="When do you want your website to launch?"
                value={formData.launchDate}
                onChange={(e) => setFormData({ ...formData, launchDate: e.target.value })}
                className={inputClass}
              />
            </div>

            {/* Terms */}
            <div className="mt-4 mb-5">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={formData.agreedToTerms}
                    onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-[18px] h-[18px] rounded border border-white/20 bg-white/5 peer-checked:bg-[#F5C518] peer-checked:border-[#F5C518] transition-all flex items-center justify-center group-hover:border-white/40">
                    {formData.agreedToTerms && (
                      <i className="ri-check-line text-[#1a1a1a] text-xs"></i>
                    )}
                  </div>
                </div>
                <span className="text-white/50 text-xs leading-relaxed">
                  I understand that by accepting a free website build, I agree to the hosting fee required to keep the site live. Hosting is $59.99/monthly or $499.99/yearly. I can cancel anytime. Read full{' '}
                  <Link to="/terms" className="text-[#F5C518] hover:text-[#D4A90E] underline cursor-pointer" onClick={(e) => e.stopPropagation()}>
                    Terms &amp; Conditions
                  </Link>.
                </span>
              </label>
            </div>

            {submitError && (
              <p className="text-red-400 text-xs mb-4 bg-red-400/10 border border-red-400/20 rounded-lg px-4 py-2">
                {submitError}
              </p>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <button
                type="submit"
                disabled={!formData.agreedToTerms || isSubmitting}
                className={`w-full sm:w-auto px-8 py-3 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                  formData.agreedToTerms && !isSubmitting
                    ? 'bg-[#F5C518] text-[#1a1a1a] hover:bg-[#D4A90E] shadow-lg shadow-[#F5C518]/20'
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    Submit Inquiry
                    <i className="ri-arrow-right-up-line text-base"></i>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="w-full sm:w-auto text-white/40 hover:text-white/70 px-6 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap border border-white/10 hover:border-white/25"
              >
                Maybe Later
              </button>
            </div>
          </form>
        ) : (
          <div className="px-6 sm:px-8 pb-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#F5C518]/10 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-[#F5C518] text-3xl"></i>
            </div>
            <h3 className="text-white text-xl font-semibold mb-2 font-display tracking-wide">YOU&apos;RE IN!</h3>
            <p className="text-white/50 text-sm leading-relaxed">
              Your inquiry has been submitted successfully. I&apos;ll review it and reach out to you soon!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
